import type { AddressPretty } from '@cutr/constants/cutlist-theme.ts';
import {
  IframeMessagePayload,
  IframeMessageProps,
  PayloadTypes,
} from '@cutr/constants/iframe-communication';
import React from 'react';

import { Address } from '@/api/address';

import { getCountriesList } from './intl';

type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T; // from lodash

export function isTruthy<T>(value: T): value is Truthy<T> {
  return Boolean(value);
}

export function urlParamToBoolean(param: string | null) {
  if (param == null) return;

  if (['true', '1', 1].includes(param)) {
    return true;
  }
  if (['false', '0', 0].includes(param)) {
    return false;
  }
}

export function resolveTokenLoginTarget(redirect: string, cutlistId: string) {
  return redirect.startsWith('/cutlist/')
    ? redirect
    : `/cutlist/${cutlistId}/parts`;
}

export function copyToClipboard(textToCopy: string) {
  // navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(textToCopy);
  }
  return Promise.reject(new Error('Clipboard API not available'));
}

export function addrToPretty(
  address: Address,
  lang = 'en',
  email?: string
): AddressPretty {
  const countryName =
    getCountriesList(lang).find((it) => it.code === address.country)?.label ||
    'Netherlands';

  return {
    name: address.contactName,
    address: [address.line1, address.line2, address.postalCode, address.city]
      .filter(Boolean)
      .join(', '),
    countryName: countryName,
    tel: address.phone,
    email,
  };
}

export function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export function usePostMessageResponder<MessageType extends keyof PayloadTypes>(
  messageType: MessageType,
  handler: (
    payload: PayloadTypes[MessageType],
    event: MessageEvent
  ) => Promise<unknown>
) {
  React.useEffect(() => {
    const listener = async (event: MessageEvent) => {
      const { msg, payload, __msgId: messageId } = event.data;
      if (msg !== messageType) return;

      let resultPromise;
      try {
        resultPromise = Promise.resolve(handler(payload, event));
      } catch (e) {
        resultPromise = Promise.reject(e);
      }
      if (messageId) {
        resultPromise = resultPromise
          .then((result) => ({ result }))
          .catch((err) => ({ err }))
          .then((payload) => {
            const targetOrigin = event.origin || '*';
            (event.source as Window).postMessage(
              { ...payload, __msgId: messageId },
              targetOrigin
            );
          });
      }
      await resultPromise;
    };
    window.addEventListener('message', listener);
    return () => {
      window.removeEventListener('message', listener);
    };
  }, [messageType, handler]);
}

export function iframePostMessage<Key extends IframeMessagePayload['key']>(
  key: Key,
  payload: IframeMessageProps[Key]
) {
  if (!isInIframe()) return;
  if (!window.name) return;

  window.parent.postMessage(
    { __msgId: window.name, payload: { key, ...payload } },
    '*'
  );
}

export const cleanupLocalStorage = (storageSuffix?: string) => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.endsWith(storageSuffix || getPersistedKey(''))) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.error('no local storage');
  }
};

export function getPersistedKey(key: string) {
  const suffix = isInIframe() && window.name ? `:${window.name}` : '';
  return `${key}${suffix}`;
}

// currently formatting embed video urls only for youtube videos
export const formatEmbedVideoUrl = ({
  videoUrl,
}: {
  videoUrl: string;
}): string => {
  const embedUrlRoot = 'https://www.youtube.com/embed/';

  const videoId = videoUrl.split('v=')[1];

  return embedUrlRoot + videoId;
};

/**
 * Creates a deterministic JSON string by sorting object keys
 * This ensures the same object always produces the same string regardless of property order
 */
export const deterministicStringify = (obj: unknown): string => {
  if (obj === null || typeof obj !== 'object') {
    return JSON.stringify(obj);
  }

  if (Array.isArray(obj)) {
    return `[${obj.map(deterministicStringify).join(',')}]`;
  }

  // Sort keys to ensure consistent ordering
  const sortedKeys = Object.keys(obj as Record<string, unknown>).sort();
  const sortedObj = sortedKeys.map(
    (key) =>
      `"${key}":${deterministicStringify(
        (obj as Record<string, unknown>)[key]
      )}`
  );

  return `{${sortedObj.join(',')}}`;
};

/**
 * Creates a hash from an object that is consistent regardless of property order
 */
export const hashObject = (obj: unknown): number => {
  return hashString(deterministicStringify(obj));
};

export const hashString = (str: string) => {
  let hash = 5381; // djb2 starting value
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i); // hash * 33 + c
    hash = hash >>> 0; // Keep as unsigned 32-bit integer
  }
  return hash;
};

export function pick(obj: Record<string, unknown>, keys: string[]) {
  return keys.reduce((res, key) => ({ ...res, [key]: obj[key] }), {});
}

export const omit = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key as K))
  ) as Omit<T, K>;
};
